import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle2,
  PartyPopper,
  User,
  Building2,
  Package,
  Users,
  Clock,
  ArrowRight,
  Sparkles,
} from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useAuthStore, Button } from '@personic/shared'
import { PRODUCTS } from '@/components/onboarding/ProductSelector'
import { INDUSTRIES } from '@/components/onboarding/IndustrySelector'

// Confetti-like particles
function Confetti() {
  const colors = ['#C58E53', '#7C9A92', '#2E3A47', '#F5E6D3']

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {[...Array(50)].map((_, i) => {
        const color = colors[i % colors.length]
        const left = Math.random() * 100
        const delay = Math.random() * 0.5
        const duration = 2 + Math.random() * 2
        const size = 4 + Math.random() * 8

        return (
          <motion.div
            key={i}
            initial={{ y: -20, x: 0, opacity: 1, rotate: 0 }}
            animate={{
              y: '100vh',
              x: (Math.random() - 0.5) * 200,
              opacity: 0,
              rotate: Math.random() * 720 - 360,
            }}
            transition={{
              duration,
              delay,
              ease: 'easeOut',
            }}
            style={{
              position: 'absolute',
              left: `${left}%`,
              width: size,
              height: size,
              backgroundColor: color,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            }}
          />
        )
      })}
    </div>
  )
}

export default function SetupComplete() {
  const navigate = useNavigate()
  const { tenantStatus } = useAuthStore()
  const {
    profileData,
    organizationData,
    selectedProducts,
    teamInvites,
    setStep,
    reset,
  } = useOnboardingStore()
  const [showConfetti, setShowConfetti] = useState(true)

  // Ensure we're on step 6
  useEffect(() => {
    setStep(6)

    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 3000)
    return () => clearTimeout(timer)
  }, [setStep])

  const isPending = tenantStatus === 'pending'

  const handleGoToDashboard = () => {
    // Clear onboarding state
    reset()

    if (isPending) {
      navigate('/tenant/pending')
    } else {
      navigate('/tenant/dashboard')
    }
  }

  const selectedProductNames = selectedProducts
    .map((id) => PRODUCTS.find((p) => p.id === id)?.name)
    .filter(Boolean)

  const industryName =
    INDUSTRIES.find((i) => i.id === organizationData.industry)?.name || organizationData.industry

  const validInvites = teamInvites.filter((invite) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(invite.email)
  })

  return (
    <div className="text-center">
      {/* Confetti Animation */}
      {showConfetti && <Confetti />}

      {/* Success Animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
        className="relative inline-block mb-8"
      >
        <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-xl">
          <CheckCircle2 className="w-14 h-14 text-white" />
        </div>

        {/* Party Popper */}
        <motion.div
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.5, type: 'spring' }}
          className="absolute -top-2 -right-2"
        >
          <PartyPopper className="w-8 h-8 text-copper-500" />
        </motion.div>
      </motion.div>

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-midnight-900 mb-3">
          Setup Complete!
        </h1>
        <p className="text-lg text-warm-600 max-w-md mx-auto">
          {isPending
            ? "Your account is ready and awaiting approval. We'll notify you once it's active."
            : "Your account is all set up. You're ready to start using Personic AI!"}
        </p>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 text-left"
      >
        {/* Profile Summary */}
        <div className="p-4 bg-warm-50/50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-5 h-5 text-copper-500" />
            <h3 className="font-heading font-semibold text-midnight-900">Profile</h3>
          </div>
          <p className="text-sm text-midnight-700">{profileData.fullName}</p>
          {profileData.jobTitle && (
            <p className="text-sm text-warm-500">{profileData.jobTitle}</p>
          )}
        </div>

        {/* Organization Summary */}
        <div className="p-4 bg-warm-50/50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-5 h-5 text-copper-500" />
            <h3 className="font-heading font-semibold text-midnight-900">Organization</h3>
          </div>
          <p className="text-sm text-midnight-700">{organizationData.companyName}</p>
          <p className="text-sm text-warm-500">{industryName}</p>
        </div>

        {/* Products Summary */}
        <div className="p-4 bg-warm-50/50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-copper-500" />
            <h3 className="font-heading font-semibold text-midnight-900">Products</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedProductNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center px-2 py-1 bg-copper-100 text-copper-700 rounded-full text-xs font-heading"
              >
                {name}
              </span>
            ))}
          </div>
        </div>

        {/* Team Summary */}
        <div className="p-4 bg-warm-50/50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-copper-500" />
            <h3 className="font-heading font-semibold text-midnight-900">Team</h3>
          </div>
          <p className="text-sm text-midnight-700">
            {validInvites.length > 0
              ? `${validInvites.length} ${validInvites.length === 1 ? 'invitation' : 'invitations'} pending`
              : 'No team members invited yet'}
          </p>
        </div>
      </motion.div>

      {/* Pending Status Notice */}
      {isPending && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
        >
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-left">
            <h4 className="font-heading font-semibold text-amber-800">
              Account Pending Approval
            </h4>
            <p className="text-sm text-amber-700 mt-1">
              Our team will review your application and typically approve accounts within
              24 hours. You'll receive an email notification once approved.
            </p>
          </div>
        </motion.div>
      )}

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-10"
      >
        <Button
          onClick={handleGoToDashboard}
          size="lg"
          rightIcon={isPending ? <Clock size={20} /> : <ArrowRight size={20} />}
          className="min-w-[200px]"
        >
          {isPending ? 'View Status' : 'Go to Dashboard'}
        </Button>

        {!isPending && (
          <div className="flex items-center justify-center gap-2 mt-4 text-sm text-warm-500">
            <Sparkles className="w-4 h-4 text-copper-500" />
            <span>You're all set to start exploring Personic AI</span>
          </div>
        )}
      </motion.div>
    </div>
  )
}
